import { Trash2, ArrowUp, ArrowDown } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { STEP_TYPES, STATE_COLOR } from "@/lib/wf";
import { Input } from "@/components/mh/controls";

const ConditionConfig = ({ node, set, editable }) => {
  const c = node.config;
  const jev = c.mode === "jev";
  const mode = (m) => set(m === "jev" ? { config: { mode: "jev", question: c.question || "", threshold: c.threshold ?? 70 }, label: "Jev gate" }
    : { config: { threshold: c.threshold ?? 70 }, label: `If confidence < ${c.threshold ?? 70}%` });
  return (
    <div className="space-y-3">
      <div className="flex rounded-full bg-white/[0.04] border border-white/10 p-1 text-xs" data-testid="inspector-condition-mode">
        {[["confidence", "Confidence"], ["jev", "Ask Jev"]].map(([k, t]) => (
          <button key={k} disabled={!editable} onClick={() => mode(k)} data-testid={`condition-mode-${k}`}
            className={`flex-1 h-7 rounded-full transition-colors ${(jev ? "jev" : "confidence") === k ? "bg-white text-black" : "text-muted-foreground hover:text-foreground"}`}>{t}</button>
        ))}
      </div>
      {jev ? (
        <>
          <label className="block text-xs text-muted-foreground">Yes/no question about the previous step's receipt
            <textarea value={c.question || ""} disabled={!editable} rows={3} data-testid="inspector-jev-question" placeholder="Did the previous step find at least one strong ICP-fit account?"
              onChange={(e) => set({ config: { ...c, question: e.target.value } })}
              className="mt-1 w-full rounded-lg bg-white/[0.03] border border-white/10 p-2.5 text-sm text-foreground outline-none focus:border-white/30" />
          </label>
          <Input label="Continue automatically when Jev is at least (% yes)" type="number" min={1} max={99} value={c.threshold ?? 70} disabled={!editable} testId="inspector-jev-threshold"
            onChange={(e) => set({ config: { ...c, threshold: Number(e.target.value) } })} />
          <p className="text-[11px] text-muted-foreground">Below that, a missing receipt, or a "blocked" receipt → the step goes to a human on the desk.</p>
        </>
      ) : (
        <Input label="Ask a human if confidence is below (%)" type="number" min={1} max={99} value={c.threshold ?? 70} disabled={!editable} testId="inspector-threshold"
          onChange={(e) => set({ config: { threshold: Number(e.target.value) }, label: `If confidence < ${e.target.value}%` })} />
      )}
    </div>
  );
};

export const NodeInspector = ({ node, agents, onChange, onDelete, editable }) => {
  if (!node) return <p className="text-sm text-muted-foreground" data-testid="wf-inspector-empty">Select a step to edit it. To connect two steps, click the right dot of one, then the left dot of the next. Click a line to remove it.</p>;
  const set = (patch) => onChange({ ...node, ...patch });
  return (
    <div className="space-y-4" data-testid="wf-inspector">
      <div className="font-mono text-[10px] uppercase tracking-[0.24em]" style={{ color: STEP_TYPES[node.type].color }}>{STEP_TYPES[node.type].label}</div>
      {node.type === "agent" && (
        <Select value={node.agent_slug || undefined} onValueChange={(v) => set({ agent_slug: v, label: agents.find((a) => a.slug === v)?.name || v })} disabled={!editable}>
          <SelectTrigger className="h-10 rounded-lg bg-white/[0.03] border-white/10" data-testid="inspector-agent"><SelectValue placeholder="Choose an agent" /></SelectTrigger>
          <SelectContent className="bg-[#0A0A0F] border-white/10 max-h-72">{agents.map((a) => <SelectItem key={a.slug} value={a.slug}>{a.name}</SelectItem>)}</SelectContent>
        </Select>
      )}
      <Input label="Label" value={node.label} onChange={(e) => set({ label: e.target.value })} disabled={!editable} testId="inspector-label" />
      {node.type === "condition" && <ConditionConfig node={node} set={set} editable={editable} />}
      {node.type === "delay" && (
        <Input label="Wait (seconds, max 60)" type="number" min={1} max={60} value={node.config.seconds ?? 5} disabled={!editable} testId="inspector-seconds"
          onChange={(e) => set({ config: { seconds: Number(e.target.value) }, label: `Wait ${e.target.value}s` })} />
      )}
      {editable && <button onClick={onDelete} data-testid="inspector-delete" className="inline-flex items-center gap-2 text-sm text-[#FF3B5C] hover:underline"><Trash2 className="w-4 h-4" /> Remove step</button>}
    </div>
  );
};

export const MobileSteps = ({ list, agentsBy, runStates, onMove, onRemove, onSelect, selected, editable }) => (
  <ol className="space-y-2" data-testid="wf-mobile-list">
    {list.map((n, i) => (
      <li key={n.id} onClick={() => onSelect(n.id)} data-testid={`wf-mobile-step-${i}`}
        className="flex items-center gap-3 p-3 rounded-xl border bg-[#0B0B11]" style={{ borderColor: selected === n.id ? "rgba(255,255,255,0.6)" : runStates?.[n.id] && runStates[n.id] !== "pending" ? STATE_COLOR[runStates[n.id]] : "rgba(255,255,255,0.1)" }}>
        <span className="font-mono text-[10px] text-muted-foreground w-5">{i + 1}</span>
        <div className="flex-1 min-w-0">
          <div className="font-mono text-[9px] uppercase tracking-[0.2em]" style={{ color: STEP_TYPES[n.type].color }}>{STEP_TYPES[n.type].label}{runStates?.[n.id] ? ` · ${runStates[n.id]}` : ""}</div>
          <div className="text-sm truncate">{n.label}</div>
          {n.type === "agent" && <div className="text-[11px] text-muted-foreground truncate">{agentsBy[n.agent_slug]?.stage || "No agent picked"}</div>}
        </div>
        {editable && (
          <div className="flex items-center gap-1">
            <button onClick={(e) => { e.stopPropagation(); onMove(i, -1); }} disabled={i === 0} className="w-9 h-9 grid place-items-center rounded-lg border border-white/10 disabled:opacity-30" data-testid={`wf-mobile-up-${i}`}><ArrowUp className="w-4 h-4" /></button>
            <button onClick={(e) => { e.stopPropagation(); onMove(i, 1); }} disabled={i === list.length - 1} className="w-9 h-9 grid place-items-center rounded-lg border border-white/10 disabled:opacity-30" data-testid={`wf-mobile-down-${i}`}><ArrowDown className="w-4 h-4" /></button>
            <button onClick={(e) => { e.stopPropagation(); onRemove(n.id); }} className="w-9 h-9 grid place-items-center rounded-lg border border-white/10 text-[#FF3B5C]" data-testid={`wf-mobile-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
          </div>
        )}
      </li>
    ))}
    {list.length === 0 && <p className="text-sm text-muted-foreground py-4">No steps yet. Tap “Add step”.</p>}
  </ol>
);
